/**
 * AstrologerCard — the "Find out more... ask" widget (ASTROLOGER_CARD).
 *
 * Pure presentational; design tokens only (renders inside `.askk-theme`).
 * Call/Chat actions fire an analytics event then hand the server-provided
 * deeplink to the native bridge. A single action fills the full width.
 *
 * "best" cards (badge present) get:
 *  - a 1px gradient border (strokeGradientTertiary) at 20% opacity, per Figma.
 *    It's a separate layer BEHIND the card (so opacity doesn't fade content);
 *    the opaque card sits 1px inset and reveals it as the border.
 *  - a radial glow at the top (surfaceGradientTertiary stops, composed radial).
 *
 * socialProof.html is BE-provided HTML. We do NOT use dangerouslySetInnerHTML
 * (it would fire inline handlers like `<img onerror>` and could exfiltrate the
 * JS-readable session cookies). Instead renderSocialProof() parses it safely and
 * honors only a `<font color>` highlight — see that helper.
 */
import { MouseEvent, ReactNode } from 'react'
import { AstrologerAction, AstrologerCardData } from '../../api/types'
import { navigate } from '../../lib/bridge'
import { AppEvent, flushEvents, logEvent, logEvents } from '../../lib/analytics'
import { EventAction, EventComponent } from '../../lib/eventConstants'
import { takePageCloseEvent } from '../lifecycle'
import { ChatIcon, PhoneIcon } from '../icons'

const SECTION = 'ASTROLOGER_CARD'

/**
 * Safely render the BE social-proof HTML. The design only uses `<font color>` to
 * highlight the count, e.g. `<font color="#FFFFFF">2,000+</font> spoke today`.
 *
 * DOMParser builds a DETACHED document — it does not execute scripts, load
 * resources, or fire events — so parsing is safe. We then rebuild React nodes
 * honoring only a `<font>` tag's validated hex `color`; every other tag collapses
 * to its text content (and its attributes/handlers are dropped entirely). No HTML
 * is ever injected into the live DOM.
 */
function renderSocialProof(html: string): ReactNode {
  if (typeof DOMParser === 'undefined') return null
  const body = new DOMParser().parseFromString(html, 'text/html').body
  return Array.from(body.childNodes).map((node, i) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent
    if (node.nodeName === 'FONT') {
      const color = (node as Element).getAttribute('color')
      const safe = color && /^#[0-9a-fA-F]{3,8}$/.test(color) ? color : undefined
      return (
        <span key={i} style={safe ? { color: safe } : undefined}>
          {node.textContent}
        </span>
      )
    }
    return node.textContent // any other tag → keep only its text
  })
}

interface Props {
  data: AstrologerCardData
}

export function AstrologerCard({ data }: Props) {
  const { header, astrologer } = data
  const isBest = !!astrologer.badge

  // Pseudo-click on the card as a whole (NOT the CTAs) — captures interest with
  // no feedback. CTA clicks stopPropagation so they don't also fire this.
  const handleCardClick = () => {
    logEvent({ section: SECTION, action: EventAction.CLICK, component: EventComponent.CARD })
  }

  const handleAction = async (event: MouseEvent, action: AstrologerAction) => {
    event.stopPropagation()
    // This redirect tears down the WebView, which would cancel in-flight event
    // POSTs. So fire the CTA click + the page close in ONE batched request and
    // WAIT for it to flush (capped) BEFORE handing the deeplink to the bridge.
    const batch: AppEvent[] = [
      {
        section: SECTION,
        action: EventAction.CLICK,
        // `cta_audio` | `cta_chat` — the mediaType is encoded in the component.
        component: `cta_${action.mediaType}`,
      },
    ]
    const closeEvent = takePageCloseEvent()
    if (closeEvent) batch.push(closeEvent)

    try {
      // keepalive: this send races the WebView teardown caused by navigate().
      await flushEvents([logEvents(batch, { keepalive: true })])
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('[AstrologerCard] CTA analytics flush failed:', error)
      }
    } finally {
      // Navigation must happen regardless — analytics must never block the CTA.
      navigate(action.deeplink)
    }
  }

  // Card contents (glow + badge + body) — same regardless of border treatment.
  const body = (
    <>
      {/* Radial glow at the top (surfaceGradientTertiary stops); fades around the
          name. Always present — independent of the badge. */}
      <div className="pointer-events-none absolute inset-x-0 -top-4 z-0 h-[104px] [background-image:radial-gradient(60%_100%_at_50%_0%,var(--gradient-surface-tertiary-stops))]" />

      {astrologer.badge && (
        <div className="absolute right-0 top-4 z-20 flex items-center gap-0.5 rounded-l-lg bg-gradient-strokeGradientTertiary p-1">
          {astrologer.badge.iconUrl && (
            <img src={astrologer.badge.iconUrl} alt="" className="h-3.5 w-3.5" />
          )}
          {astrologer.badge.label && (
            <span className="text-labelXSmall text-surfaceZero">{astrologer.badge.label}</span>
          )}
        </div>
      )}

      <div className="relative z-10 flex flex-col gap-4">
        {/* Avatar + info */}
        <div className="flex items-center gap-4 px-4">
          {astrologer.avatarUrl && (
            <img
              src={astrologer.avatarUrl}
              alt={astrologer.name}
              className="h-[76px] w-[76px] shrink-0 rounded-lg object-cover"
            />
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-col">
              {header && <p className="text-titleSmall text-textPrimary">{header}</p>}
              <p className="text-titleSmall text-textPrimary">{astrologer.name}</p>
            </div>
            {astrologer.socialProof && (
              <div className="flex items-center gap-1">
                {astrologer.socialProof.imageUrl && (
                  <img src={astrologer.socialProof.imageUrl} alt="" className="h-5 w-auto" />
                )}
                {astrologer.socialProof.html && (
                  <span className="text-bodySmall text-textSecondary">
                    {renderSocialProof(astrologer.socialProof.html)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* CTAs — a single action fills the full width (flex-1) */}
        <div className="flex items-center gap-2 px-4">
          {astrologer.actions.map((action) => (
            <button
              key={action.mediaType}
              type="button"
              onClick={(e) => handleAction(e, action)}
              className="flex h-10 flex-1 items-center justify-center gap-1 rounded-full bg-accentPrimary px-4 text-textInversePrimary"
            >
              {action.iconUrl ? (
                <img src={action.iconUrl} alt="" className="h-[18px] w-[18px]" />
              ) : action.mediaType === 'audio' ? (
                <PhoneIcon className="h-[18px] w-[18px]" />
              ) : (
                <ChatIcon className="h-[18px] w-[18px]" />
              )}
              <span className="text-labelLarge">{action.label}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  )

  if (!isBest) {
    return (
      <div
        onClick={handleCardClick}
        className="relative overflow-hidden rounded-3xl bg-surfaceOneVariant py-4"
      >
        {body}
      </div>
    )
  }

  // "best" → 20% gradient border behind, opaque card inset 1px to reveal it.
  return (
    <div className="relative rounded-3xl">
      <div className="pointer-events-none absolute inset-0 z-0 rounded-3xl bg-gradient-strokeGradientTertiary opacity-20" />
      <div
        onClick={handleCardClick}
        className="relative z-10 m-px overflow-hidden rounded-[23px] bg-surfaceOneVariant py-4"
      >
        {body}
      </div>
    </div>
  )
}

export default AstrologerCard
